import { BUILD_INFO, type BuildInfo } from './buildInfo'

const BUILD_INFO_URL = '/build-info.json'
const BUILD_REFRESH_PARAM = '__jli_build'
const BUILD_REFRESH_SESSION_KEY = '__jli_build_refresh_target__'

type HistoryLike = Pick<History, 'replaceState'>
type LocationLike = Pick<Location, 'href' | 'replace'>
type SessionStorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>

export interface EnsureLatestBuildOptions {
  buildInfo?: BuildInfo
  fetchImpl?: typeof fetch
  history?: HistoryLike
  location?: LocationLike
  now?: () => number
  sessionStorage?: SessionStorageLike
}

const normalizeSha = (value?: string | null) => String(value ?? '').trim()

const getUrlWithoutBuildRefreshParam = (url: URL) => {
  url.searchParams.delete(BUILD_REFRESH_PARAM)
  return `${url.pathname}${url.search}${url.hash}`
}

const clearBuildRefreshParam = ({
  currentSha,
  history,
  location,
  sessionStorage,
}: {
  currentSha: string
  history: HistoryLike
  location: LocationLike
  sessionStorage: SessionStorageLike
}) => {
  const currentUrl = new URL(location.href)
  const refreshParam = currentUrl.searchParams.get(BUILD_REFRESH_PARAM)

  if (!refreshParam || !currentSha.startsWith(refreshParam)) {
    return
  }

  history.replaceState(null, '', getUrlWithoutBuildRefreshParam(currentUrl))
  sessionStorage.removeItem(BUILD_REFRESH_SESSION_KEY)
}

export const ensureLatestBuild = async ({
  buildInfo = BUILD_INFO,
  fetchImpl = fetch,
  history = window.history,
  location = window.location,
  now = () => Date.now(),
  sessionStorage = window.sessionStorage,
}: EnsureLatestBuildOptions = {}) => {
  const currentSha = normalizeSha(buildInfo.gitSha)

  if (currentSha === '') {
    return false
  }

  clearBuildRefreshParam({
    currentSha,
    history,
    location,
    sessionStorage,
  })

  try {
    const response = await fetchImpl(`${BUILD_INFO_URL}?ts=${now()}`, {
      cache: 'no-store',
    })

    if (!response.ok) {
      return false
    }

    const latestBuildInfo = (await response.json()) as Partial<BuildInfo>
    const latestSha = normalizeSha(latestBuildInfo.gitSha)

    if (latestSha === '' || latestSha === currentSha) {
      sessionStorage.removeItem(BUILD_REFRESH_SESSION_KEY)
      return false
    }

    if (sessionStorage.getItem(BUILD_REFRESH_SESSION_KEY) === latestSha) {
      return false
    }

    sessionStorage.setItem(BUILD_REFRESH_SESSION_KEY, latestSha)

    const refreshUrl = new URL(location.href)
    refreshUrl.searchParams.set(BUILD_REFRESH_PARAM, latestSha.slice(0, 12))
    location.replace(refreshUrl.toString())

    return true
  } catch {
    return false
  }
}
